import React, { useState, useCallback, useMemo, useRef } from 'react';

import { moveTaskTo } from 'utils/task';
import { getMovedTaskOrder } from 'services/store-service';

export const TasksDNDContext = React.createContext();

export const TasksDNDProvider = ({ withMilestone, children }) => {
    const [draggedProps, setDraggedProps] = useState(null);
    const draggedLastProps = useRef(null);
    const isDropped = useRef(true);

    const handleMove = useCallback(async ({ source, destination, save = true }) => {
        if (save) {
            setDraggedProps(null);

            isDropped.current = true;

            if (!draggedProps || !draggedLastProps.current) {
                return;
            }

            moveTaskTo(draggedProps, draggedLastProps.current, true);
            draggedLastProps.current = null;
            return;
        }

        if (!destination) {
            return;
        }

        const nextTaskOrder = getMovedTaskOrder(source.id, destination.taskGroupId, destination.id, destination.isBefore);

        const nextDestination = {
            order: nextTaskOrder,
            archived: destination.archived || destination.taskGroupId === 'archived',
            completed: destination.completed || destination.taskGroupId === 'completed',
            taskGroupId: destination.taskGroupId,
            taskGroupTaskProps: destination.taskGroupTaskProps,
        };

        if (withMilestone) {
            nextDestination.milestoneId = destination.milestoneId;
        }

        draggedLastProps.current = nextDestination;
        requestAnimationFrame(() => {
            moveTaskTo(source, nextDestination, false);
        });
    }, [draggedProps, draggedLastProps]);

    const handleDragStart = useCallback(taskProps => {
        isDropped.current = false;

        setTimeout(() => {
            setDraggedProps(taskProps);
        });
    }, []);

    const handleDragEnd = useCallback(() => {
        if (isDropped.current) {
            return;
        }

        handleMove({ save: true });
    }, [isDropped, handleMove]);

    const draggedId = useMemo(() => draggedProps?.id, [draggedProps]);

    const value = useMemo(() => ({
        onMove: handleMove,
        onDragStart: handleDragStart,
        onDragEnd: handleDragEnd,
        draggedId
    }), [handleMove, handleDragStart, handleDragEnd, draggedId]);

    return (
        <TasksDNDContext.Provider value={value}>
            { children }
        </TasksDNDContext.Provider>
    );
}
